/**
 * Intake, as the patient app calls it (MASTER_PLAN M8.7, API.md §2.3 and §2.4,
 * FR-INTAKE-03, FR-INTAKE-04, ADR-017, ADR-018).
 *
 * Seven routes, one client, and the same shared transport as `booking.ts` and
 * `payments.ts` — so every call carries `credentials: 'include'` and therefore the
 * `ha_draft` cookie (the funnel) or the patient session cookie (the portal). Which cookie
 * matters is the server's business; this file never names one.
 *
 * | Method | Route | Identity |
 * |---|---|---|
 * | `publishedForm` | `GET /public/intake/form` | none — public, per clinic |
 * | `currentFunnelPacket` | `GET /public/intake/current` | `ha_draft` |
 * | `startFunnelPacket` | `POST /public/intake/submissions` | `ha_draft` |
 * | `saveFunnelAnswers` | `PATCH /public/intake/current` | `ha_draft` |
 * | `completeFunnelIntake` | `POST /public/intake/current/complete` | `ha_draft` |
 * | `appointmentPacket` | `GET /patient/appointments/:id/intake` | patient session |
 * | `saveAppointmentAnswers` | `PUT /patient/appointments/:id/intake` | patient session |
 *
 * ## The response is the authority, not the request
 *
 * Every write answers with the whole `IntakePacket`. That is not a convenience: the API
 * merges a partial patch into what is stored and then validates the **merged** set
 * against the pinned definition, which is what makes conditional visibility work —
 * answering "no" to "do you have insurance?" drops the four answers underneath it
 * (INTAKE_FORMS.md §8A.2). A caller that kept its own copy of the answers after a save
 * would keep the four the server just dropped, so every call site here re-seeds from the
 * packet it was handed.
 *
 * ## Why the bodies are typed from `@platform/contracts`
 *
 * The same reason `booking.ts` and `payments.ts` give: these controllers take
 * `@Body() body: unknown` and validate with zod, so Nest's Swagger plugin emits
 * `requestBody?: never` and `client.post` would refuse a body it will happily send.
 * Responses are read as the contract's own `IntakePacket` rather than as the generated
 * structural type, because the generated `definition` is an expansion of
 * `FormDefinitionSchema` that no `FormRenderer` prop will accept — and the contract is
 * the shape both sides parse with, so a drift is a compile error at the call site.
 */

import { isApiError, type ApiClient } from '@platform/api-client';
import type {
  AnswerSet,
  CreateSubmissionRequest,
  IntakePacket,
  PublishedForm,
  SaveIntakeAnswersRequest,
} from '@platform/contracts';

import { apiClient } from './client';

const PUBLIC = '/api/v1/public/intake';
const PATIENT = '/api/v1/patient/appointments';

export interface IntakeClient {
  /**
   * The clinic's published definition and the version it came from.
   *
   * Cached by the API for sixty seconds. The form id is not the version id and both come
   * back, because "save my answers against this" has to name the **version**: a publish
   * between page load and first keystroke must not move the patient onto new questions.
   */
  publishedForm(formKey?: string, signal?: AbortSignal): Promise<PublishedForm>;

  /**
   * Resume: this browser's submission, or `null`.
   *
   * `null` is the ordinary answer for a visitor who has not reached the paperwork step,
   * and — after `confirm` — for a browser whose draft has been converted. It is never an
   * error, and it is the one call a page may make without creating anything.
   */
  currentFunnelPacket(signal?: AbortSignal): Promise<IntakePacket | null>;

  /**
   * Start (or re-read) the submission this browser's draft owns.
   *
   * **Creates a booking draft when there is none** (the controller's `#draftId` ensures
   * one), so it is called only once a page knows the funnel is live — never merely
   * because a route was opened. `Idempotency-Key` makes a double submit one submission.
   */
  startFunnelPacket(
    input?: CreateSubmissionRequest,
    idempotencyKey?: string,
  ): Promise<IntakePacket>;

  /** Autosave. A partial set, merged server-side and validated in `partial` mode. */
  saveFunnelAnswers(answers: AnswerSet): Promise<IntakePacket>;

  /**
   * Full validation. `422 INTAKE_INCOMPLETE` carries `details.errors` by field id.
   *
   * On the funnel this stamps `submitted_at` and leaves the status `in_progress`: until
   * there is a visit there is nothing for the paperwork to be the paperwork *for*, and
   * `confirm` is what attaches the appointment (INTAKE_FORMS.md §8A.4).
   */
  completeFunnelIntake(answers?: AnswerSet): Promise<IntakePacket>;

  /** `GET /patient/appointments/:id/intake` — created on first read, pinned version. */
  appointmentPacket(appointmentId: string, signal?: AbortSignal): Promise<IntakePacket>;

  /** `PUT /patient/appointments/:id/intake`. `submit: true` finishes and fully validates. */
  saveAppointmentAnswers(
    appointmentId: string,
    answers: AnswerSet,
    submit: boolean,
  ): Promise<IntakePacket>;
}

/**
 * The same answers, without the holes.
 *
 * `AnswerSet` is hand-written in `@platform/contracts` as `Record<string, AnswerValue |
 * undefined>` — because a renderer holding a half-filled form genuinely has keys with no
 * value — while the *wire* type is the schema's own `Record<string, AnswerValue>`. The
 * two meet here. Dropping an `undefined` rather than sending it is also what the server
 * would do with it: `validateAnswers` treats a blank as unanswered and omits it from
 * `values` (INTAKE_FORMS.md §4).
 */
export function definedAnswers(answers: AnswerSet): SaveIntakeAnswersRequest['answers'] {
  const out: SaveIntakeAnswersRequest['answers'] = {};
  for (const [fieldId, value] of Object.entries(answers)) {
    if (value !== undefined) out[fieldId] = value;
  }
  return out;
}

export function createIntakeClient(client: ApiClient): IntakeClient {
  const withSignal = (signal: AbortSignal | undefined): { signal?: AbortSignal } =>
    signal === undefined ? {} : { signal };

  const save = (answers: AnswerSet, submit: boolean): SaveIntakeAnswersRequest => ({
    answers: definedAnswers(answers),
    submit,
  });

  return {
    publishedForm: async (formKey, signal) => {
      const body = await client.request<{ data: { form: PublishedForm } }>(
        'get',
        `${PUBLIC}/form`,
        undefined,
        {
          ...withSignal(signal),
          ...(formKey === undefined ? {} : { query: { formKey } }),
        },
      );
      return body.data.form;
    },

    currentFunnelPacket: async (signal) => {
      const body = await client.request<{ data: IntakePacket | null }>(
        'get',
        `${PUBLIC}/current`,
        undefined,
        withSignal(signal),
      );
      return body.data;
    },

    startFunnelPacket: async (input = {}, idempotencyKey) => {
      const body = await client.request<{ data: IntakePacket }>(
        'post',
        `${PUBLIC}/submissions`,
        input,
        idempotencyKey === undefined ? {} : { idempotencyKey },
      );
      return body.data;
    },

    saveFunnelAnswers: async (answers) => {
      const body = await client.request<{ data: IntakePacket }>(
        'patch',
        `${PUBLIC}/current`,
        save(answers, false),
      );
      return body.data;
    },

    completeFunnelIntake: async (answers = {}) => {
      const body = await client.request<{ data: IntakePacket }>(
        'post',
        `${PUBLIC}/current/complete`,
        save(answers, false),
      );
      return body.data;
    },

    appointmentPacket: async (appointmentId, signal) => {
      const body = await client.request<{ data: IntakePacket }>(
        'get',
        `${PATIENT}/${encodeURIComponent(appointmentId)}/intake`,
        undefined,
        withSignal(signal),
      );
      return body.data;
    },

    saveAppointmentAnswers: async (appointmentId, answers, submit) => {
      const body = await client.request<{ data: IntakePacket }>(
        'put',
        `${PATIENT}/${encodeURIComponent(appointmentId)}/intake`,
        save(answers, submit),
      );
      return body.data;
    },
  };
}

/** The app's own instance, over the single client and query cache. */
export const intakeClient: IntakeClient = createIntakeClient(apiClient);

/* ------------------------------------------------------------------ *
 * Failures
 * ------------------------------------------------------------------ */

/**
 * The per-field sentences the API refused a save with, keyed by field id.
 *
 * `400 VALIDATION_FAILED` on an autosave and `422 INTAKE_INCOMPLETE` on a complete both
 * carry `details.errors` in `validateAnswers`' own wording. They are shown verbatim and
 * are **not** re-derived in the browser: the two copies of the rules are the same
 * function, and a screen that re-ran its own would eventually disagree with the one that
 * decides (INTAKE_FORMS.md §8A.2, rules §17.2).
 *
 * An empty object when the failure carries none, so a call site can branch on `size`
 * rather than on a code.
 */
export function intakeFieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error)) return {};
  const raw = error.details?.['errors'];
  if (typeof raw !== 'object' || raw === null) return {};

  const out: Record<string, string> = {};
  for (const [field, message] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof message === 'string') out[field] = message;
  }
  return out;
}

/** `422 INTAKE_INCOMPLETE` — answers are still missing. The form stays open. */
export function isIntakeIncomplete(error: unknown): boolean {
  return isApiError(error) && error.code === 'INTAKE_INCOMPLETE';
}

/**
 * `410 DRAFT_EXPIRED` — this browser's booking draft is gone.
 *
 * Also the answer after `confirm`, which converts the draft inside the booking
 * transaction: the paperwork then belongs to the appointment and is finished in the
 * portal, not in the funnel (`Docs/BOOKING_FUNNEL_WIRING.md` §7).
 */
export function isDraftExpired(error: unknown): boolean {
  return isApiError(error) && error.code === 'DRAFT_EXPIRED';
}

/** A sentence for the patient. The API writes its messages to be shown (API.md §1.6). */
export function describeIntakeError(error: unknown): string {
  if (isApiError(error)) return error.message;
  return 'We could not reach the clinic. Please check your connection and try again.';
}
